import { useState, useEffect, useMemo } from "react";
import styles from "../RightSidebar/rightsidebar.module.css";
import checkoutStyles from "../salesCheckout/checkoutSidebar.module.css";
import { appendExpandedTimeLabel, formatMonthYear, getInitials, getConsistentColor, scrollToBookingCard, mapSaleStatus, removedSeconds } from "../../Utils/utils";
import Guesticon from "../../Assets/images/guestIcon.svg";
import BookingDetails from "../BookingDetails/BookingDetails";
import BookingPaymentDetails from "../BookingPaymentDetails/BookingPaymentDetails";
import PrimiumMemberIcon from "../Icons/PrimiumMemberIcon";
import MemberRateBadge from "../Badges/MemberRateBadge";
import { SaleNote } from "../SaleNote/SaleNote";
import { SaleCancelReason } from "../SaleCancelReason/SaleCancelReason";
import { useGlobalContext } from "../../Context/GlobalContext";
import styles1 from '../Badges/RequestedTherapist.module.css'
import StarIcon from '../Icons/StarIcon'
import { getPaymentDetails } from "../../APICalling/APICalling";
import { useNavigate } from "react-router-dom";
import { isPrimaryCustomerGroup } from "../../Utils/liveGuestCheckinTag";

const SaleBookingInfo = ({ bookingId, bookingDetails, bookingDataFromAPI, customerDataFromAPI, saleNote, showAddNotePopup, onAddPayment, disableServiceListHover = false, isSalesFlow = false, onOpenRelatedSale }) => {
	const navigate = useNavigate();
	const { userName, setAmountDue: setGlobalAmountDue, setServiceTotal, setPaymentHistory, selectedOutletData, selectedDate } = useGlobalContext();
	const mappedStatus = mapSaleStatus(bookingDataFromAPI?.status || '');
	const normalizedStatus = String(bookingDataFromAPI?.status || '').trim().toLowerCase();
	const normalizedPaymentStatus = String(bookingDataFromAPI?.payment_status || '').trim().toLowerCase();
	const normalizeStatusKey = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, '');
	const statusMatches = (value, statuses) => statuses.includes(normalizeStatusKey(value));
	const productPartialStatuses = ['partial', 'open(inprogress)'];
	const productClosedStatuses = ['closed', 'paid', 'completed'];
	const isRefundedOrVoided =
		normalizedStatus === 'voided' ||
		normalizedStatus === 'refunded' ||
		normalizedPaymentStatus === 'voided' ||
		normalizedPaymentStatus === 'refunded';

	const isCustomerDeleted = bookingDataFromAPI?.user_name === `deleted_user_${bookingDataFromAPI?.user_id}`;

	const isProductSale = bookingDataFromAPI?.booking_type === 'product' || bookingDataFromAPI?.type === 'product' || Array.isArray(bookingDataFromAPI?.product_items) || Array.isArray(bookingDataFromAPI?.voucher_items);
	const isProductPartialLikeStatus = isProductSale && ( statusMatches(bookingDataFromAPI?.status, productPartialStatuses) || statusMatches(bookingDataFromAPI?.payment_status, productPartialStatuses) || normalizedStatus === 'open' || normalizedPaymentStatus === 'open');
	const isProductClosedLikeStatus = isProductSale && (
		statusMatches(bookingDataFromAPI?.status, productClosedStatuses) || statusMatches(bookingDataFromAPI?.payment_status, productClosedStatuses)
	);
	
	const [paymentTransactions, setPaymentTransactions] = useState([]);
	const [amountDue, setAmountDue] = useState(0);
	const [liveUserNotes, setLiveUserNotes] = useState(bookingDataFromAPI?.users_notes || "");
	const saleOrBookingId = bookingDataFromAPI?.booking_id || bookingDataFromAPI?.id;
	const refundAudit = bookingDataFromAPI?.refund_audit;
	const cancelReason = bookingDataFromAPI?.cancel_reason || bookingDetails?.cancel_reason;
	const cancelRemark = bookingDataFromAPI?.cancel_remark || bookingDetails?.cancel_remark;
	const displayCancelReason = cancelReason || refundAudit?.reason;
	const displayCancelRemark = cancelReason ? cancelRemark : refundAudit?.remark;
	const paymentQueryParams = useMemo(() => {
		if (!isProductSale || !saleOrBookingId) return undefined;
		return { sale_id: saleOrBookingId };
	}, [isProductSale, saleOrBookingId]);


	// Fetch payment transactions and calculate amount due
	const fetchPayments = async () => {
		if (!saleOrBookingId) return;
		if (!isProductPartialLikeStatus) return;
		const isVoucherSale =Array.isArray(bookingDataFromAPI?.voucher_items) && bookingDataFromAPI.voucher_items.length > 0;
		if (bookingId && !isVoucherSale && Number(saleOrBookingId) !== Number(bookingId)) return;
		
		try {
			const response = await getPaymentDetails(saleOrBookingId, paymentQueryParams);
			const transactions = response?.data?.data || [];
			setPaymentTransactions(transactions);
			
			// Calculate total paid
			const totalPaid = transactions.reduce((sum, payment) => {
				return sum + parseFloat(payment.amount_value || 0);
			}, 0);
			
			// Format payment history for global context
			const formattedHistory = transactions.map(payment => ({
				method: payment.payment_method,
				amount: parseFloat(payment.amount_value || 0),
				id: payment.booking_id
			}));
			setPaymentHistory(formattedHistory);
			
		// Calculate amount due (ensure it's never negative)
		const grandTotal = parseFloat(bookingDataFromAPI?.grand_total || 0);
		const remaining = grandTotal - totalPaid;
		const calculatedDue = remaining > 0 ? remaining : 0;
		
		setAmountDue(calculatedDue);
		// Also update global context for PaymentPage
		setGlobalAmountDue(calculatedDue.toFixed(2));
		} catch (error) {
			console.error('Error fetching payment details:', error);
		}
	};
	
	useEffect(() => {
		fetchPayments();
		// Set serviceTotal for PaymentPage calculations
		const subtotal = parseFloat(bookingDataFromAPI?.sub_total || 0);
		setServiceTotal(subtotal);
	}, [bookingId, saleOrBookingId, bookingDataFromAPI?.grand_total, bookingDataFromAPI?.sub_total, normalizedStatus, normalizedPaymentStatus, paymentQueryParams]);

	useEffect(() => {
		const handlePaymentsRefresh = (event) => {
			if (event?.type === 'salesPaymentSuccess') {
				const eventSaleId = event?.detail?.saleId ?? event?.detail?.bookingId ?? event?.detail?.id;
				if (eventSaleId != null && Number(eventSaleId) !== Number(saleOrBookingId)) return;
			}
			fetchPayments();
		};
		window.addEventListener('voidSaleCompleted', handlePaymentsRefresh);
		window.addEventListener('salesPaymentSuccess', handlePaymentsRefresh);
		return () => {
			window.removeEventListener('voidSaleCompleted', handlePaymentsRefresh);
			window.removeEventListener('salesPaymentSuccess', handlePaymentsRefresh);
		};
	}, [saleOrBookingId, bookingDataFromAPI?.grand_total, isProductPartialLikeStatus, paymentQueryParams]);

	useEffect(() => {
		setLiveUserNotes(bookingDataFromAPI?.users_notes || "");
	}, [bookingDataFromAPI?.users_notes, bookingDataFromAPI?.booking_id]);

	useEffect(() => {
		const handleCustomerNotesUpdated = (event) => {
			const updatedCustomerId = event?.detail?.customerId;
			const updatedNotes = event?.detail?.notes ?? "";
			if (!updatedCustomerId) return;
			if (Number(updatedCustomerId) !== Number(bookingDataFromAPI?.user_id)) return;
			setLiveUserNotes(updatedNotes);
		};

		window.addEventListener('customerNotesUpdated', handleCustomerNotesUpdated);
		return () => {
			window.removeEventListener('customerNotesUpdated', handleCustomerNotesUpdated);
		};
	}, [bookingDataFromAPI?.user_id]);

	const safeParseAdditional = (val) => {
		if (!val) return {};
		if (typeof val === 'object') return val || {};
		try { return JSON.parse(val); } catch { return {}; }
	};

	const getServiceTimeDisplay = (booking) => {
		let displayTime = "";
		if (booking?.service_time) {
			displayTime = removedSeconds(booking.service_time);
		} else if (booking?.start_time) {
			const [h, m] = String(booking.start_time).split(':');
			const hour = parseInt(h, 10);
			const h12 = hour % 12 || 12;
			const ampm = hour >= 12 ? 'PM' : 'AM';
			displayTime = `${String(h12).padStart(2, '0')}:${(m || '00').slice(0, 2)} ${ampm}`;
		} else if (booking?.service_at) {
			const part = booking.service_at.split(' ')[1];
			if (part) {
				const [hh, mm] = part.split(':');
				const hour = parseInt(hh, 10);
				const h12 = hour % 12 || 12;
				const ampm = hour >= 12 ? 'PM' : 'AM';
				displayTime = `${String(h12).padStart(2, '0')}:${(mm || '00').slice(0, 2)} ${ampm}`;
			}
		}
		const labelContextDate = !isSalesFlow
			? (selectedDate || booking?.service_date || bookingDataFromAPI?.service_date)
			: (booking?.service_date || bookingDataFromAPI?.service_date);
		return appendExpandedTimeLabel(
			displayTime,
			booking,
			selectedOutletData?.timing,
			labelContextDate
		);
	};

	// Transform customer data to group split services under parent
	const transformCustomerData = (customerData) => {
		if (!customerData) return {};
		const transformedData = {};

		Object.entries(customerData).forEach(([customerName, bookings]) => {
			const transformedBookings = [];
			const processedBookingIds = new Set();

			bookings.forEach((booking) => {
				if (processedBookingIds.has(booking.booking_item_id)) return;
				const additionalInfo = safeParseAdditional(booking.additional);

				if ((additionalInfo.splited === true || additionalInfo.splited === "true") && booking.parent_booking_item_id === null) {
					const childServices = bookings.filter((childBooking) => {
						const childAdditional = safeParseAdditional(childBooking.additional);
						return childBooking.parent_booking_item_id === booking.booking_item_id &&
							(childAdditional.splited === true || childAdditional.splited === "true");
					});

					childServices.forEach((child) => processedBookingIds.add(child.booking_item_id));

					transformedBookings.push({
						...booking,
						SplitedService: childServices
					});
				} else if (additionalInfo.splited === false || additionalInfo.splited === "false" || booking.parent_booking_item_id !== null) {
					const isChildOfSplitService = bookings.some((parentBooking) => {
						const parentAdditional = safeParseAdditional(parentBooking.additional);
						return parentBooking.booking_item_id === booking.parent_booking_item_id &&
							(parentAdditional.splited === true || parentAdditional.splited === "true");
					});

					if (!isChildOfSplitService) {
						transformedBookings.push(booking);
					}
				}
			});

			transformedData[customerName] = transformedBookings;
		});

		return transformedData;
	};

	const updatedCustomerDataFromAPI = transformCustomerData(customerDataFromAPI);
	const parentSaleId = bookingDataFromAPI?.parent_sale_id;


	return (
		<div className={styles.infoWrapper} style={{ paddingTop: 0 }}>
			<div className={styles.outletName}>
				<label className={styles.label}>Location :</label>
				<p className={styles.sameFont}>{bookingDataFromAPI?.outlet_name}</p>
			</div>

			{parentSaleId && (
				<div className={styles.refundSaleInfoHeader}>
					This refund is for{" "}
					<button
						type="button"
						className={styles.refundSaleInfoHeaderButton}
						onClick={() => onOpenRelatedSale?.(parentSaleId)}
					>
						Sale #{parentSaleId}
					</button>
				</div>
			)}

			{/* Customer */}
			<div className={styles.customerInfoWrapper}>
				<div className={styles.customerCard}>
					<div className={styles.avatarWrapper}>
						<div className={styles.avatar} style={{backgroundColor: getConsistentColor(bookingDataFromAPI?.user_id, bookingDataFromAPI?.user_name)}}>
							<span>{getInitials(bookingDataFromAPI?.user_name)}</span>
							{bookingDataFromAPI?.membership === 1 && (
								<span className={styles.membershipIcon}>
									<PrimiumMemberIcon/>
								</span>
							)}
						</div>
					</div>
					<div 
						className={styles.customerDetails}
						onClick={() => {
							const contactNumber = bookingDataFromAPI?.user_contact_number || bookingDataFromAPI?.contact_number || bookingDataFromAPI?.user_number;
							if (contactNumber || bookingDataFromAPI?.user_id) {
								const customerData = {
									id: bookingDataFromAPI?.user_id,
									name: bookingDataFromAPI?.user_name,
									contact_number: contactNumber,
									email: bookingDataFromAPI?.email,
								};
								
								const event = new CustomEvent('openCustomerDetails', {
									detail: { customer: customerData }
								});
								window.dispatchEvent(event);
							}
						}}
						style={{ cursor: (bookingDataFromAPI?.user_contact_number || bookingDataFromAPI?.contact_number || bookingDataFromAPI?.user_number || bookingDataFromAPI?.user_id) ? 'pointer' : 'default' }}
					>
						<div className={styles.customerHeaderRow}>
							<p className={styles.customerMobile}>{bookingDataFromAPI?.user_contact_number || bookingDataFromAPI?.contact_number || bookingDataFromAPI?.user_number || ''}</p>
							<p className={styles.customerName}>{bookingDataFromAPI?.user_name}</p>
						</div>
						{liveUserNotes?.trim() && (
							<div className={styles.customerNotes}>
								<p className={styles.customerNotesText}>{liveUserNotes}</p>
							</div>
						)}
						{isCustomerDeleted && (
							<div className={styles.customerDeleted}>
								<p className={styles.customerDeletedText}>Customer Deleted</p>
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Sale Notes Section */}
			{saleNote?.trim() && (
				<SaleNote saleNote={saleNote} showAddNotePopup={showAddNotePopup} />
			)}

			{/* Products - for product bookings */}
			{!isRefundedOrVoided && bookingDataFromAPI?.product_items && bookingDataFromAPI.product_items.length > 0 && (
				<div className={styles.paxBlock}>
					<div className={styles.paxHeader}>
						<h3>Products Purchased</h3>
					</div>
					{bookingDataFromAPI.product_items.map((product, index) => {
						const unitPrice = parseFloat(product.price || 0);
						const quantity = Number(product.quantity) || 1;
						const isMembershipApplied =
							Number(product.membership ?? product.isMembershipApplied ?? 0) === 1;
						const totalPrice = product.total_price != null
							? parseFloat(product.total_price)
							: (() => {
								const effectiveUnit = product.change_price != null
									? parseFloat(product.change_price)
									: unitPrice;
								return effectiveUnit * quantity;
							})();
						const effectiveUnitPrice = quantity > 0 ? totalPrice / quantity : unitPrice;
						const hasMemberDiscount = isMembershipApplied && effectiveUnitPrice < unitPrice;
						const hasManualPriceChange =
							!isMembershipApplied &&
							product.change_price != null &&
							parseFloat(product.change_price) !== unitPrice;
						const showStrikethrough = hasMemberDiscount || hasManualPriceChange;
						const displayUnitPrice = showStrikethrough ? effectiveUnitPrice : unitPrice;

						return (
							<div key={index} className={styles.selectedServiceCard}>
								<div className={styles.selectedServiceHead}>
									<div className={styles.productHeaderRow}>
										<div>
											<h3>{product.product_name}</h3>
											{isMembershipApplied && (
												<div className={styles.memberRateBadgeContainer}>
													<MemberRateBadge text="Member Rate" />
												</div>
											)}
										</div>
										<p className={styles.price}>
											{showStrikethrough && (
												<span style={{ textDecoration: 'line-through', color: '#999', marginRight: '8px' }}>
													${unitPrice.toFixed(2)}
												</span>
											)}
											<span>${displayUnitPrice.toFixed(2)}</span>
										</p>
									</div>
								</div>
								<div className={styles.selectedTherapistCard}>
									<div className={styles.productDetailRow}>
										<label className={styles.label}>Quantity:</label>
										<p className={styles.sameFont}>{product.quantity}</p>
									</div>
									<div className={styles.productDetailRow}>
										<label className={styles.label}>Total:</label>
										<p className={styles.sameFont}>${totalPrice.toFixed(2)}</p>
									</div>
								</div>
								{product.reason && (
									<div className={styles.productReason}>
										<label className={styles.label}>Reason to Change Price:</label>
										<p className={styles.sameFont}>{product.reason}</p>
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}

			{/* Vouchers - for product bookings with voucher sales */}
			{!isRefundedOrVoided && bookingDataFromAPI?.voucher_items && bookingDataFromAPI.voucher_items.length > 0 && (
				<div className={styles.paxBlock}>
					<div className={styles.paxHeader}>
						<h3>Vouchers Purchased</h3>
					</div>
					{bookingDataFromAPI.voucher_items.map((voucher, index) => {
						const unitPrice = parseFloat(voucher.price || 0);
						const quantity = Number(voucher.quantity) || 1;
						const totalPrice = voucher.total_price != null
							? parseFloat(voucher.total_price)
							: unitPrice * quantity;

						return (
							<div key={voucher.voucher_id ?? index} className={styles.selectedServiceCard}>
								<div className={styles.selectedServiceHead}>
									<div className={styles.productHeaderRow}>
										<div>
											<h3>{voucher.product_name || 'Voucher'}</h3>
										</div>
										<p className={styles.price}>
											<span>${unitPrice.toFixed(2)}</span>
										</p>
									</div>
								</div>
								<div className={styles.voucherDetails}>
									{voucher.serial_number && (
										<div className={styles.voucherSerialRow}>
											<label className={styles.label}>Serial Number:</label>
											<p className={styles.voucherSerialValue}>{voucher.serial_number}</p>
										</div>
									)}
									<div className={styles.selectedTherapistCard}>
										<div className={styles.productDetailRow}>
											<label className={styles.label}>Quantity:</label>
											<p className={styles.sameFont}>{quantity}</p>
										</div>
										<div className={styles.productDetailRow}>
											<label className={styles.label}>Total:</label>
											<p className={styles.sameFont}>${totalPrice.toFixed(2)}</p>
										</div>
									</div>
								</div>
							</div>
						);
					})}
				</div>
			)}

			{/* Services */}
			{!isRefundedOrVoided && (() => {
				const primaryCustomerData = [];
				const guestData = [];
				if (updatedCustomerDataFromAPI) {
					Object.entries(updatedCustomerDataFromAPI).forEach(([customerName, bookings]) => {
						if (isPrimaryCustomerGroup(customerName, bookings, bookingDataFromAPI?.user_name)) {
							primaryCustomerData.push({ customerName, bookings });
						} else {
							guestData.push({ customerName, bookings });
						}
					});
				}

				const renderServiceTopRow = (additionalInfo, booking) => (
					<div className={styles.selectedServiceHead}>
						<div className="d-flex justify-between">
							<h3>{`${additionalInfo?.duration ?? ''} Mins ${booking.service_name}`}</h3>
							<p className={styles.price}><span>${parseFloat(booking.price || 0).toFixed(2)}</span></p>
						</div>
					</div>
				);

				const renderTherapistRow = (booking, genderKey = 'therapist_gender', pagerKey = 'therapist_pager_number', nameKey = 'therapist_name', requestedFlag) => (
					<div className={`${styles.selectedTherapistCard} d-flex justify-between`}>
						<div className={`${styles.dFlex}`}>
							<label className={styles.label}>With:</label>
							<span className={`${styles.therapistCircle} ${booking?.[genderKey] === "female" ? styles.female : styles.male}`}>
								{booking?.[pagerKey]}
							</span>
							<p className={styles.sameFont}>{booking?.[nameKey]}</p>
						</div>
						{requestedFlag === 1 && 
						   <div className={styles1.requestedTherapist}>
						   <StarIcon/>
						   Requested Therapist
					   </div>}
					</div>
				);

				const renderPriceMeta = (booking, additionalInfo = {}) => {
					const commissionSource = booking?.commission ?? additionalInfo?.commission;
					const commissionValue = Number(commissionSource?.rate ?? commissionSource ?? 0);
					const isMemberRateApplied = booking?.isMemberRateApplied ?? additionalInfo?.isMemberRateApplied;
					const isMembershipApplied = booking?.isMembershipApplied ?? additionalInfo?.isMembershipApplied;
					const rawPriceAdjustment = booking?.price_adjustment ?? additionalInfo?.price_adjustment ?? booking?.discount_option?.item ?? booking?.discount_option ?? additionalInfo?.discount_option?.item ?? additionalInfo?.discount_option;
					const priceAdjustment = rawPriceAdjustment?.item ? rawPriceAdjustment.item : rawPriceAdjustment;
					const priceAdjustmentTitle = priceAdjustment?.title ?? priceAdjustment?.name ?? priceAdjustment?.label ?? "";

					return (
						<>
							{commissionValue > 0 && (
								<div className={`${styles.commissionCard} ${styles.dFlex} ${styles.spacing}`}>
									<label className={styles.label}>Commission:</label>
									<p className={styles.sameFont}>${commissionValue.toFixed(2)}</p>
								</div>
							)}
							{((isMemberRateApplied === 1 || isMemberRateApplied === true) ||
								((isMemberRateApplied === 0 || isMemberRateApplied === false) &&
									(isMembershipApplied === 1 || isMembershipApplied === true)) ||
								priceAdjustmentTitle) && (
								<div className={styles.memberRateBadgeContainer}>
									{(isMemberRateApplied === 1 || isMemberRateApplied === true) && (
										<MemberRateBadge text="Member Rate (OP)" />
									)}
									{(isMemberRateApplied === 0 || isMemberRateApplied === false) &&
										(isMembershipApplied === 1 || isMembershipApplied === true) && (
											<MemberRateBadge text="Member Rate" />
									)}
									{priceAdjustmentTitle && (
										<MemberRateBadge text={priceAdjustmentTitle} />
									)}
								</div>
							)}
						</>
					);
				};

				const renderSplitServices = (splitedServices = []) => (
					splitedServices.length > 0 && (
						<div className={styles.splitedService}>
							{splitedServices.map((splitedService) => {
								const splitedAdditionalInfo = safeParseAdditional(splitedService.additional);
								return (
									<div key={splitedService.booking_item_id}>
										{renderTherapistRow(splitedService, 'therapist_gender', 'therapist_pager_number', 'therapist_name', splitedAdditionalInfo?.requested_person)}
										<div className={`${styles.dFlex} ${styles.spacing}`}>
											<div className={`${styles.dFlex}`}>
												<label className={styles.label}>For:</label>
												<p className={styles.sameFont}>{splitedAdditionalInfo?.duration ?? ''} mins</p>
											</div>
											<div className={`${styles.dFlex} ${styles.space}`}>
												<label className={styles.label}>At:</label>
												<p className={styles.sameFont}>{getServiceTimeDisplay(splitedService)}</p>
											</div>
										</div>
									</div>
								);
							})}
						</div>
					)
				);

				return (
					<>
						{primaryCustomerData.map(({ customerName, bookings }) => (
							<div key={`primary-${customerName}`} className={styles.paxBlock}>
								{bookings.map((booking, index) => {
									const additionalInfo = safeParseAdditional(booking.additional);
									return (
										<div key={index} className={styles.serviceLine}>
											<div className={`${styles.serviceDetails} ${disableServiceListHover ? styles.serviceDetailsNoHover : ''}`}
												onClick={() => {if (!disableServiceListHover && booking?.booking_item_id) {scrollToBookingCard(booking?.booking_item_id);}}}
											>
												{renderServiceTopRow(additionalInfo, booking)}
												{renderPriceMeta(booking, additionalInfo)}
												{renderTherapistRow(booking, 'therapist_gender', 'therapist_pager_number', 'therapist_name', additionalInfo?.requested_person)}
												<div className={`${styles.dFlex} ${styles.spacing}`}>
													<div className={`${styles.dFlex}`}>
														<label className={styles.label}>For:</label>
														<p className={styles.sameFont}>{additionalInfo?.duration ?? ''} mins</p>
													</div>
													<div className={`${styles.dFlex} ${styles.space}`}>
														<label className={styles.label}>At:</label>
														<p className={styles.sameFont}>{getServiceTimeDisplay(booking)}</p>
													</div>
												</div>
												{renderSplitServices(booking?.SplitedService)}
											</div>
										</div>
									);
								})}
							</div>
						))}

						{guestData.map(({ customerName, bookings }) => (
							<div key={`guest-${customerName}`} className={styles.paxBlock}>
								<div className={`${styles.dFlex} ${styles.paxHeader}`}>
									<img src={Guesticon} alt="guestIcon" />
									<p className={styles.paxName}>{customerName}</p>
								</div>
								{bookings.map((booking, index) => {
									const additionalInfo = safeParseAdditional(booking.additional);
									return (
										<div key={index} className={styles.serviceLine}>
											<div className={`${styles.serviceDetails} ${disableServiceListHover ? styles.serviceDetailsNoHover : ''}`}
												onClick={() => {if (!disableServiceListHover && booking?.booking_item_id) {scrollToBookingCard(booking?.booking_item_id);}}}
											>
												{renderServiceTopRow(additionalInfo, booking)}
												{renderPriceMeta(booking, additionalInfo)}
												{renderTherapistRow(booking, 'gender', 'therapist_pager_number', 'therapist_name', additionalInfo?.requested_person)}
												<div className={`${styles.dFlex} ${styles.spacing}`}>
													<div className={`${styles.dFlex}`}>
														<label className={styles.label}>For:</label>
														<p className={styles.sameFont}>{additionalInfo?.duration ?? ''} mins</p>
													</div>
													<div className={`${styles.dFlex} ${styles.space}`}>
														<label className={styles.label}>At:</label>
														<p className={styles.sameFont}>{getServiceTimeDisplay(booking)}</p>
													</div>
												</div>
												{renderSplitServices(booking?.SplitedService)}
											</div>
										</div>
									);
								})}
							</div>
						))}
					</>
				);
			})()}

			{/* Cancel / refund reason */}
			{isRefundedOrVoided && displayCancelReason && (
				<SaleCancelReason reason={displayCancelReason} remark={displayCancelRemark} />
			)}

		{/* Sales summary - Show for product and closed/partial/voided states */}
		{(isProductSale || isProductPartialLikeStatus || isProductClosedLikeStatus || isRefundedOrVoided || normalizedStatus === 'completed' || normalizedStatus === 'paid') && (
			<>
				<div className={styles.priceInfoWrapper}>
					<div className={styles.upperSection}>
						{isRefundedOrVoided ? (
							<>
								<div className={`${styles.payrow} ${styles.dFlex} ${styles.justifyBetween} ${styles.p20} border-bottom`}>
									<p>Refund Amount</p>
									<p>${(parseFloat(bookingDataFromAPI?.grand_total) || 0).toFixed(2)}</p>
								</div>
								<div className={`${styles.payrow} ${styles.dFlex} ${styles.justifyBetween} ${styles.p20}`}>
									<p>Subtotal</p>
									<p>${(parseFloat(bookingDataFromAPI?.grand_total) || 0).toFixed(2)}</p>
								</div>
								<div className={`${styles.finalTotal} ${styles.payrow} ${styles.dFlex} ${styles.justifyBetween}`}>
									<p>Total Refund</p>
									<p>${(parseFloat(bookingDataFromAPI?.grand_total) || 0).toFixed(2)}</p>
								</div>
							</>
						) : (
							<>
								<div className={`${styles.payrow} ${styles.dFlex} ${styles.justifyBetween} ${styles.p20}`}>
									<p>Subtotal</p>
									<p>${(parseFloat(bookingDataFromAPI?.sub_total) || 0).toFixed(2)}</p>
								</div>
								{parseFloat(bookingDataFromAPI?.gst_amount || 0) > 0 && (
									<div className={`${styles.payrow} ${styles.dFlex} ${styles.justifyBetween} ${styles.p20}`}>
										<p>GST</p>
										<p>${(parseFloat(bookingDataFromAPI?.gst_amount) || 0).toFixed(2)}</p>
									</div>
								)}
								{parseFloat(bookingDataFromAPI?.cash_rounding || 0) !== 0 && (
									<div
									className={`${styles.payrow} ${styles.dFlex} ${styles.justifyBetween} ${styles.p20}`}>
									<p>Cash Rounding</p>
									<p>
									{bookingDataFromAPI?.cash_rounding < 0
									? `- $${Math.abs(bookingDataFromAPI?.cash_rounding).toFixed(2)}`
										: `$${Number(bookingDataFromAPI?.cash_rounding).toFixed(2)}`}
										</p>
									</div>
								)}
								<div className={`${styles.finalTotal} ${styles.payrow} ${styles.dFlex} ${styles.justifyBetween}`}>
									<p>Total</p>
									<p>${(parseFloat(bookingDataFromAPI?.sub_total || 0) + parseFloat(bookingDataFromAPI?.gst_amount || 0) - parseFloat(bookingDataFromAPI?.cash_rounding || 0)).toFixed(2)}</p>
								</div>
							</>
						)}
					</div>
				</div>

				<div className={styles.paymentDetail}>
				<BookingPaymentDetails key={`sale-payments-${bookingDataFromAPI?.sale_id || saleOrBookingId}`} bookingID={saleOrBookingId} from='sale-booking-info' paymentQueryParams={paymentQueryParams} saleId={bookingDataFromAPI?.sale_id || saleOrBookingId} childSaleId={bookingDataFromAPI?.child_sale_id ?? null} parentSaleId={bookingDataFromAPI?.parent_sale_id ?? null} refundMode={isRefundedOrVoided} />
				</div>
				
				{/* Show Amount Due if balance remaining (> 1 cent) */}
				{!isRefundedOrVoided && amountDue > 0.01 && (
					<div className={styles.priceInfoWrapper} style={{ marginTop: '10px', marginBottom: 0 }}>
						<div className={styles.upperSection}>
							<div className={`${styles.finalTotal} ${styles.payrow} ${styles.dFlex} ${styles.justifyBetween}`}>
								<p>Amount Due</p>
								<p>${amountDue.toFixed(2)}</p>
							</div>
						</div>
					</div>
				)}
			</>
		)}


			{!isProductPartialLikeStatus && (() => {
				// Build a combined datetime from service_date (DD-MM-YYYY) and service_time (hh:mm:ss AM/PM)
				const serviceDate = bookingDataFromAPI?.service_date; // e.g. 05-09-2025
				const serviceTime = bookingDataFromAPI?.service_time; // e.g. 06:45:00 PM
				let composedDateTime = "";
				if (serviceDate && serviceTime) {
					const [dd, mm, yyyy] = serviceDate.split("-");
					// Convert to ISO-friendly: YYYY-MM-DD hh:mm:ss
					composedDateTime = `${yyyy}-${mm}-${dd} ${serviceTime}`;
				}
			

				return (
					<BookingDetails
						heading={isRefundedOrVoided ? "Refund Details" : "Sale Details"}
						CreateDate={bookingDataFromAPI?.booking_created_at ?? bookingDataFromAPI?.created_at}
						CreatedBy={bookingDataFromAPI?.created_by_name}
						UpdatedDate={bookingDataFromAPI?.updated_at}
						LastUpdatedBy={bookingDataFromAPI?.updated_by_name}
						BookingDate={bookingDataFromAPI?.booking_date ?? bookingDetails?.booking_date}
						Source={bookingDataFromAPI?.source}
						isProductSale={isProductSale}
						Status={bookingDataFromAPI?.status || bookingDataFromAPI?.payment_status}
					/>
				);
			})()}

			{/* Payment Button at very bottom if balance remaining */}
			{isProductPartialLikeStatus && 
			 amountDue > 0.01 && 
			 onAddPayment && (
				<div className={checkoutStyles.footerProductCheckout}>
					<button
						className={checkoutStyles.primaryCta}
						onClick={() => {
							const subtotal = parseFloat(bookingDataFromAPI?.sub_total || 0);
							setServiceTotal(subtotal);
							setGlobalAmountDue(amountDue.toFixed(2));
							
							// Call parent handler to navigate to payment step
							onAddPayment();
						}}
					>
						Go to Payment
					</button>
				</div>
			)}
		</div>
	);
};

export default SaleBookingInfo;
